import React from 'react';
import {GeneratedVideoChunk} from '../../../data/models/LibraryFile/GeneratedVideoChunk';
import EditChunkOptions from './EditChunkOptions';
import {EditChunkOption} from '../../../data/repositories/LibraryRepository';
import styles from './ChunkCard.module.css';
import Lottie from 'lottie-react';
import {loadingLottie} from '../../../assets/lottie';
import Tooltip2 from '../../../uiToolkit/Tooltip2/Tooltip2';
import {appText} from '../../../assets/text/appText';

export type ChunkVisualState = 'normal' | 'selected' | 'dimmed' | 'deleted';

interface Props {
  chunk: GeneratedVideoChunk;
  state: ChunkVisualState;
  onEditChunk: (option: EditChunkOption, chunkId: string) => void;
  onSelectChunk: (chunk: GeneratedVideoChunk) => void;
  pixelsPerSecond: number;
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
}

const ChunkCard: React.FC<Props> = ({
  chunk,
  state = 'normal',
  onEditChunk,
  onSelectChunk,
  pixelsPerSecond,
  dragHandleProps,
}) => {
  const [isLoading, setIsLoading] = React.useState(true);

  const getStateStyle = (state: string) => {
    switch (state) {
      case 'deleted':
        return styles.deleted;
      case 'selected':
        return styles.selected;
      case 'dimmed':
        return styles.dimmed;
      case 'normal':
      default:
        return styles.normal;
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-no-select]')) return;
    onSelectChunk(chunk);
  };

  const width = pixelsPerSecond * chunk.duration;
  const isTiny = width < 40;
  return state === 'deleted' ? null : (
    <div
      style={{width: `${width}px`}}
      className={`${styles.chunkCardContainer} ${getStateStyle(state)} ${
        isTiny ? styles.tinyChunk : ''
      }`}
      onClick={handleClick}>
      <div className={styles.dragHandle} {...dragHandleProps}>
        <Tooltip2 text={appText.reorder}>⠿</Tooltip2>
      </div>
      {chunk.include && (
        <EditChunkOptions
          chunk={chunk}
          onEditChunk={onEditChunk}
          isTiny={isTiny}
        />
      )}
      {chunk.isDraft && chunk.include && (
        <div className={styles.editedBadge}>{appText.edited}</div>
      )}
      <div className={styles.chunkDuration}>
        {Math.round(chunk.duration) + 's'}
      </div>
      {isLoading && (
        <Lottie
          animationData={loadingLottie}
          loop={true}
          style={{width: '34px', height: '34px'}}
        />
      )}
      <video
        src={chunk.uri}
        width={width}
        height={100}
        onLoadedData={() => setIsLoading(false)}
        style={{display: isLoading ? 'none' : 'block'}}
      />
    </div>
  );
};

// Extract base URL without query parameters (signed URLs have different signatures each time)
const getBaseUrl = (url: string | undefined): string => {
  if (!url) return '';
  const questionMarkIndex = url.indexOf('?');
  return questionMarkIndex === -1 ? url : url.substring(0, questionMarkIndex);
};

// Custom comparison to prevent re-renders when chunk data hasn't changed
const arePropsEqual = (prevProps: Props, nextProps: Props) => {
  const prevChunk = prevProps.chunk;
  const nextChunk = nextProps.chunk;

  // Compare base URLs without signature parameters
  const prevBaseUri = getBaseUrl(prevChunk.uri);
  const nextBaseUri = getBaseUrl(nextChunk.uri);

  return (
    prevChunk.id === nextChunk.id &&
    prevBaseUri === nextBaseUri &&
    prevChunk.duration === nextChunk.duration &&
    prevChunk.include === nextChunk.include &&
    prevChunk.isDraft === nextChunk.isDraft &&
    prevChunk.fileStatus === nextChunk.fileStatus &&
    prevChunk.order === nextChunk.order &&
    prevProps.state === nextProps.state &&
    prevProps.pixelsPerSecond === nextProps.pixelsPerSecond
  );
};

export default React.memo(ChunkCard, arePropsEqual);
